/**
 * Notification Store
 * Zustand store for notification state management
 * PRD-11: Dashboard Experience - C21 Notification Center
 */

import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import {
  Notification,
  NotificationFilters,
  NotificationPreferences,
  NotificationStatus,
  NotificationStore,
} from "../types/notification.types";

// ============================================================================
// Default Values
// ============================================================================

const defaultFilters: NotificationFilters = {
  types: [],
  priority: null,
  status: null,
  dateRange: {
    start: null,
    end: null,
  },
};

const defaultPreferences: NotificationPreferences = {
  email_enabled: true,
  push_enabled: true,
  sms_enabled: false,
  quiet_hours_enabled: false,
  quiet_hours_start: "22:00",
  quiet_hours_end: "08:00",
  type_settings: {
    trade: {
      enabled: true,
      email: true,
      push: true,
      sound: true,
      priority_threshold: 3,
    },
    agent: {
      enabled: true,
      email: true,
      push: true,
      sound: true,
      priority_threshold: 3,
    },
    emergency: {
      enabled: true,
      email: true,
      push: true,
      sound: true,
      priority_threshold: 1,
    },
    system: {
      enabled: true,
      email: false,
      push: true,
      sound: false,
      priority_threshold: 2,
    },
    account: {
      enabled: true,
      email: true,
      push: true,
      sound: false,
      priority_threshold: 3,
    },
    strategy: {
      enabled: true,
      email: false,
      push: true,
      sound: false,
      priority_threshold: 3,
    },
    risk: {
      enabled: true,
      email: true,
      push: true,
      sound: true,
      priority_threshold: 2,
    },
    achievement: {
      enabled: true,
      email: false,
      push: true,
      sound: false,
      priority_threshold: 4,
    },
  },
};

const getNotificationStatus = (notification: Notification): NotificationStatus =>
  notification.status ?? (notification.is_read ? "read" : "unread");

// ============================================================================
// Store Creation
// ============================================================================

export const useNotificationStore = create<NotificationStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        notifications: [],
        unreadCount: 0,
        isLoading: false,
        error: null,
        isConnected: false,
        preferences: defaultPreferences,
        filters: defaultFilters,

        // Data Management
        setNotifications: (notifications: Notification[]) => {
          // Filter out any undefined/null values and ensure each notification has is_read property
          const validNotifications = (notifications || []).filter(
            (n): n is Notification => n != null && typeof n === "object"
          );
          // The list never owns the badge: snapshots are capped (WS limit 50,
          // REST page 20), so counting them wrote a truncated number over the
          // server's real total (aside prod audit: badge oscillated 212↔50).
          // unreadCount is written only by setUnreadCount (server total) and
          // the per-item increment/decrement paths.
          set({ notifications: validNotifications }, false, "setNotifications");
        },

        addNotification: (notification: Notification) => {
          set(
            (state) => {
              const existing = state.notifications.find(
                (entry) => entry.id === notification.id,
              );
              if (existing) {
                return {
                  notifications: state.notifications.map((entry) =>
                    entry.id === notification.id
                      ? { ...entry, ...notification }
                      : entry,
                  ),
                  unreadCount:
                    existing.is_read && !notification.is_read
                      ? state.unreadCount + 1
                      : !existing.is_read && notification.is_read
                        ? Math.max(0, state.unreadCount - 1)
                        : state.unreadCount,
                };
              }
              return {
                notifications: [notification, ...state.notifications],
                unreadCount: notification.is_read
                  ? state.unreadCount
                  : state.unreadCount + 1,
              };
            },
            false,
            "addNotification",
          );
        },

        updateNotification: (id: Notification["id"], updates: Partial<Notification>) => {
          set(
            (state) => ({
              notifications: state.notifications.map((n) =>
                n.id === id ? { ...n, ...updates } : n,
              ),
            }),
            false,
            "updateNotification",
          );
        },

        removeNotification: (id: Notification["id"]) => {
          set(
            (state) => {
              const notification = state.notifications.find((n) => n.id === id);
              const wasUnread = notification && !notification.is_read;
              return {
                notifications: state.notifications.filter((n) => n.id !== id),
                unreadCount: wasUnread
                  ? Math.max(0, state.unreadCount - 1)
                  : state.unreadCount,
              };
            },
            false,
            "removeNotification",
          );
        },

        // Read Status
        markAsRead: (id: Notification["id"]) => {
          set(
            (state) => {
              const notification = state.notifications.find((n) => n.id === id);
              if (!notification || notification.is_read) return state;

              return {
                notifications: state.notifications.map((n) =>
                  n.id === id
                    ? {
                        ...n,
                        is_read: true,
                        status: "read" as NotificationStatus,
                        read_at: new Date().toISOString(),
                      }
                    : n,
                ),
                unreadCount: Math.max(0, state.unreadCount - 1),
              };
            },
            false,
            "markAsRead",
          );
        },

        markAllAsRead: () => {
          set(
            (state) => ({
              notifications: state.notifications.map((n) => ({
                ...n,
                is_read: true,
                status: "read" as NotificationStatus,
                read_at: n.read_at || new Date().toISOString(),
              })),
              unreadCount: 0,
            }),
            false,
            "markAllAsRead",
          );
        },

        markAsUnread: (id: Notification["id"]) => {
          set(
            (state) => {
              const notification = state.notifications.find((n) => n.id === id);
              if (!notification || !notification.is_read) return state;

              return {
                notifications: state.notifications.map((n) =>
                  n.id === id
                    ? {
                        ...n,
                        is_read: false,
                        status: "unread" as NotificationStatus,
                        read_at: undefined,
                      }
                    : n,
                ),
                unreadCount: state.unreadCount + 1,
              };
            },
            false,
            "markAsUnread",
          );
        },

        // Connection Status
        setConnected: (connected: boolean) => {
          set({ isConnected: connected }, false, "setConnected");
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, "setLoading");
        },

        setError: (error: string | null) => {
          set({ error }, false, "setError");
        },

        // Filters
        setFilters: (filters: Partial<NotificationFilters>) => {
          set(
            (state) => ({
              filters: { ...state.filters, ...filters },
            }),
            false,
            "setFilters",
          );
        },

        clearFilters: () => {
          set({ filters: defaultFilters }, false, "clearFilters");
        },

        // Preferences
        setPreferences: (preferences: NotificationPreferences) => {
          set({ preferences }, false, "setPreferences");
        },

        // Count
        setUnreadCount: (count: number) => {
          set({ unreadCount: count }, false, "setUnreadCount");
        },

        decrementUnreadCount: () => {
          set(
            (state) => ({
              unreadCount: Math.max(0, state.unreadCount - 1),
            }),
            false,
            "decrementUnreadCount",
          );
        },

        incrementUnreadCount: () => {
          set(
            (state) => ({
              unreadCount: state.unreadCount + 1,
            }),
            false,
            "incrementUnreadCount",
          );
        },

        // Bulk Operations
        clearAll: () => {
          set(
            {
              notifications: [],
              unreadCount: 0,
            },
            false,
            "clearAll",
          );
        },

        archiveAll: () => {
          set(
            (state) => ({
              notifications: state.notifications.map((n) => ({
                ...n,
                status: "archived" as NotificationStatus,
              })),
            }),
            false,
            "archiveAll",
          );
        },
      }),
      {
        name: "notification-store",
        // Only persist preferences, not the notifications themselves
        partialize: (state) => ({
          preferences: state.preferences,
          filters: state.filters,
        }),
      },
    ),
    { name: "NotificationStore" },
  ),
);

// ============================================================================
// Selectors
// ============================================================================

export const selectNotifications = (state: NotificationStore) =>
  state.notifications;

export const selectUnreadCount = (state: NotificationStore) =>
  state.unreadCount;

export const selectUnreadNotifications = (state: NotificationStore) =>
  state.notifications.filter((n) => n && !n.is_read);

export const selectReadNotifications = (state: NotificationStore) =>
  state.notifications.filter((n) => n && n.is_read);

export const selectFilteredNotifications = (state: NotificationStore) => {
  const { notifications, filters } = state;

  return notifications.filter((n) => {
    // Skip null/undefined notifications
    if (!n) return false;

    // Filter by type
    if (
      filters.types.length > 0 &&
      !filters.types.includes(n.notification_type)
    ) {
      return false;
    }

    // Filter by priority
    if (filters.priority !== null && n.priority !== filters.priority) {
      return false;
    }

    // Filter by status
    if (filters.status !== null && getNotificationStatus(n) !== filters.status) {
      return false;
    }

    // Filter by date range
    if (filters.dateRange.start) {
      const notificationDate = new Date(n.created_at);
      const startDate = new Date(filters.dateRange.start);
      if (notificationDate < startDate) return false;
    }

    if (filters.dateRange.end) {
      const notificationDate = new Date(n.created_at);
      const endDate = new Date(filters.dateRange.end);
      if (notificationDate > endDate) return false;
    }

    return true;
  });
};

export const selectNotificationsByType = (
  state: NotificationStore,
  type: string,
) => state.notifications.filter((n) => n && n.notification_type === type);

export const selectCriticalNotifications = (state: NotificationStore) =>
  state.notifications.filter(
    (n) => n && (n.priority === "critical" || n.priority === "urgent") && !n.is_read,
  );

export const selectIsConnected = (state: NotificationStore) =>
  state.isConnected;

export const selectIsLoading = (state: NotificationStore) => state.isLoading;

export const selectError = (state: NotificationStore) => state.error;

export const selectPreferences = (state: NotificationStore) =>
  state.preferences;

// ============================================================================
// Granular Selector Hooks
// ============================================================================
// Following performance-optimization patterns to prevent full-store re-renders
// These hooks only re-render when the specific slice of state they select changes

// State Hooks
export const useNotifications = () =>
  useNotificationStore((state) => state.notifications);

export const useUnreadCount = () =>
  useNotificationStore((state) => state.unreadCount);

export const useNotificationFilters = () =>
  useNotificationStore((state) => state.filters);

export const useNotificationPreferences = () =>
  useNotificationStore((state) => state.preferences);

export const useNotificationIsLoading = () =>
  useNotificationStore((state) => state.isLoading);

export const useNotificationError = () =>
  useNotificationStore((state) => state.error);

export const useNotificationIsConnected = () =>
  useNotificationStore((state) => state.isConnected);

// Action Hooks
export const useSetNotifications = () =>
  useNotificationStore((state) => state.setNotifications);

export const useAddNotification = () =>
  useNotificationStore((state) => state.addNotification);

export const useMarkAsRead = () =>
  useNotificationStore((state) => state.markAsRead);

export const useMarkAllAsRead = () =>
  useNotificationStore((state) => state.markAllAsRead);

export const useSetNotificationFilters = () =>
  useNotificationStore((state) => state.setFilters);

export const useClearNotificationFilters = () =>
  useNotificationStore((state) => state.clearFilters);

// ============================================================================
// Export Default
// ============================================================================

export default useNotificationStore;
